import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import formatDate from './utils/formatDate';

/*const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};*/

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const ValuationPdfPreviewSection = ({ data }) => {
  if (!data?.header) {
    return (
      <Alert severity="warning">
        No hay datos disponibles para previsualizar el PDF.
      </Alert>
    );
  }

  const { header, items = [], orders = [], adjustments = [] } = data;
  const symbol = header.monsim || 'S/';

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Box display="flex" alignItems="center" gap={1.2} mb={2}>
        <PictureAsPdfIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Vista previa de datos para PDF
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta sección refleja la información estructurada que el backend entrega para el documento PDF.
          </Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, mb: 2.5 }}>
        <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
          Cabecera del documento
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Código
            </Typography>
            <Typography fontWeight="bold">
              VAL-{String(header.valcabid).padStart(6, '0')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Proyecto
            </Typography>
            <Typography>{header.pronom || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Moneda
            </Typography>
            <Typography>{header.moncod || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Periodo
            </Typography>
            <Typography>
              {formatDate(header.valcabfecini)} - {formatDate(header.valcabfecfin)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Estado
            </Typography>
            <Typography>{header.valcabest || '-'}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Descripción
            </Typography>
            <Typography>
              {header.valcabdes || 'Sin descripción'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Ítems
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {items.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Órdenes
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {orders.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Ajustes
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {adjustments.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Typography variant="h6" fontWeight="bold" mt={1}>
              {formatCurrency(header.valcabtot, symbol)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2.5 }} />

      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
        Ítems de valorización
      </Typography>

      {items.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          No hay ítems para mostrar.
        </Alert>
      ) : (
        <Stack spacing={1.5} mb={2.5}>
          {items.map((item) => (
            <Paper key={item.valdetid} variant="outlined" sx={{ p: 1.8, borderRadius: 2.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={1}
              >
                <Box>
                  <Typography fontWeight="bold">{item.serpronom}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cantidad: {item.valdetcan} | Unidad: {item.serprouni || '-'}
                  </Typography>
                </Box>
                <Chip
                  icon={<DescriptionIcon />}
                  label={formatCurrency(item.valdetmon, symbol)}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
        Órdenes incluidas
      </Typography>

      {orders.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          No hay órdenes para mostrar.
        </Alert>
      ) : (
        <Stack spacing={1.2} mb={2.5}>
          {orders.map((order) => (
            <Paper key={order.ordsercabid} variant="outlined" sx={{ p: 1.6, borderRadius: 2.5 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Código
                  </Typography>
                  <Typography fontWeight="bold" color="primary">
                    OS-{String(order.ordsercabid).padStart(6, '0')}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Nombre
                  </Typography>
                  <Typography>{order.ordsercabnom}</Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography>{formatDate(order.ordsercabfec)}</Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography>{order.serpronom || '-'}</Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    Precio
                  </Typography>
                  <Typography fontWeight="bold">
                    {formatCurrency(order.ordsercabpre, symbol)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
        Ajustes aplicados
      </Typography>

      {adjustments.length === 0 ? (
        <Alert severity="info">
          No existen ajustes registrados para esta valorización.
        </Alert>
      ) : (
        <Stack spacing={1.2}>
          {adjustments.map((adjustment) => (
            <Paper key={adjustment.valajuid} variant="outlined" sx={{ p: 1.6, borderRadius: 2.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={1}
              >
                <Box>
                  <Typography fontWeight="bold">{adjustment.valajudes}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipo: {adjustment.valajutip}
                  </Typography>
                </Box>

                <Typography
                  fontWeight="bold"
                  color={adjustment.valajutip === 'DESCUENTO' ? 'error.main' : 'success.main'}
                >
                  {adjustment.valajutip === 'DESCUENTO' ? '-' : '+'}
                  {formatCurrency(adjustment.valajumon, symbol)}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default ValuationPdfPreviewSection;